import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { reportsAPI, usersAPI } from '../services/api';
import './Reports.css';

const Reports = () => {
  const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await usersAPI.getAllUsers();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleDownload = async (reportType, downloadFunction, filename) => {
    setLoading({ ...loading, [reportType]: true });
    setError(null);
    setSuccess(null);

    try {
      const response = await downloadFunction();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(`${reportType} downloaded successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(`Failed to download ${reportType}. Please try again.`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading({ ...loading, [reportType]: false });
    }
  };

  const downloadAttendanceReport = () => {
    const userId = selectedEmployee || null;
    const employeeName = selectedEmployee
      ? employees.find(emp => emp.id === parseInt(selectedEmployee))?.full_name || 'employee'
      : 'all';

    handleDownload(
      'Attendance Report',
      () => reportsAPI.downloadAttendance(attendanceMonth, attendanceYear, userId),
      `attendance_${employeeName}_${attendanceYear}_${attendanceMonth}.xlsx`
    );
  };

  const downloadLeavesReport = () => {
    handleDownload(
      'Leave Report',
      () => reportsAPI.downloadLeaves(),
      `leaves_report.xlsx`
    );
  };

  const downloadEmployeesReport = () => {
    handleDownload(
      'Employee Report',
      () => reportsAPI.downloadEmployees(),
      `employees_report.xlsx`
    );
  };

  const downloadIndividualReport = () => {
    if (!selectedEmployee) {
      setError('Please select an employee first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const employee = employees.find(emp => emp.id === parseInt(selectedEmployee));
    const employeeName = employee?.full_name || 'employee';

    handleDownload(
      'Individual Employee Report',
      () => reportsAPI.downloadAttendance(attendanceMonth, attendanceYear, selectedEmployee),
      `${employeeName}_attendance_${attendanceYear}_${attendanceMonth}.xlsx`
    );
  };

  const reportCards = [
    {
      id: 'attendance',
      title: 'Attendance Report',
      description: 'Download attendance records for all employees or a specific employee',
      icon: '📅',
      color: '#635bff',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      hasFilters: true,
      hasEmployeeFilter: true,
      downloadHandler: downloadAttendanceReport
    },
    {
      id: 'individual',
      title: 'Individual Employee Report',
      description: 'Download detailed attendance report for a specific employee',
      icon: '👤',
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
      hasFilters: true,
      hasEmployeeFilter: true,
      requiresEmployee: true,
      downloadHandler: downloadIndividualReport
    },
    {
      id: 'leave',
      title: 'Leave Report',
      description: 'Download all leave records with approval status',
      icon: '🏖️',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
      hasFilters: false,
      hasEmployeeFilter: false,
      downloadHandler: downloadLeavesReport
    },
    {
      id: 'employee',
      title: 'Employee Report',
      description: 'Download complete employee directory with details',
      icon: '👥',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
      hasFilters: false,
      hasEmployeeFilter: false,
      downloadHandler: downloadEmployeesReport
    }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = [2023, 2024, 2025, 2026, 2027];

  return (
    <>
      {/* Header */}
      <div style={{
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Reports & Analytics
        </h1>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          {success}
        </Alert>
      )}

      <Row className="g-4">
        {reportCards.map((report) => (
          <Col key={report.id} lg={4} md={6} xs={12}>
            <Card className="report-card h-100">
              <div
                className="report-card-header"
                style={{ background: report.gradient }}
              >
                <div className="report-icon">{report.icon}</div>
                <h3 className="report-card-title">{report.title}</h3>
              </div>
              <Card.Body className="report-card-body">
                <p className="report-description">{report.description}</p>

                {report.hasEmployeeFilter && (
                  <div className="filters-section mb-3">
                    <Form.Group>
                      <Form.Label className="filter-label">
                        Select Employee {report.requiresEmployee ? <span className="text-danger">*</span> : '(Optional)'}
                      </Form.Label>
                      <Form.Select
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        className="filter-select"
                        required={report.requiresEmployee}
                      >
                        <option value="">{report.requiresEmployee ? 'Select an employee...' : 'All Employees'}</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.full_name} - {employee.email}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                {report.hasFilters && (
                  <div className="filters-section">
                    <Row className="g-3 mb-3">
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label className="filter-label">Month</Form.Label>
                          <Form.Select
                            value={attendanceMonth}
                            onChange={(e) => setAttendanceMonth(e.target.value)}
                            className="filter-select"
                          >
                            {months.map((month, index) => (
                              <option key={index + 1} value={index + 1}>
                                {month}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label className="filter-label">Year</Form.Label>
                          <Form.Select
                            value={attendanceYear}
                            onChange={(e) => setAttendanceYear(e.target.value)}
                            className="filter-select"
                          >
                            {years.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}

                <Button
                  className="download-btn w-100"
                  onClick={report.downloadHandler}
                  disabled={loading[report.title]}
                  style={{
                    backgroundColor: report.color,
                    borderColor: report.color
                  }}
                >
                  {loading[report.title] ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <span className="download-icon">⬇️</span>
                      Download Excel Report
                    </>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
};

export default Reports;
